import React, { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { offices, company } from "../mock/mock";

const Field = ({ label, ...props }) => (
  <div>
    <label className="mono text-[11px] uppercase tracking-widest text-neutral-500 block mb-2">{label}</label>
    <input {...props} className="w-full border-2 border-[#0A0A0A] bg-white px-4 py-3 text-sm focus:outline-none focus:shadow-[4px_4px_0_#0A0A0A] transition-shadow" />
  </div>
);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", org: "", brief: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.brief) {
      toast({ title: "Missing required fields", description: "Name, email and project brief are required." });
      return;
    }
    const saved = JSON.parse(localStorage.getItem("inf_leads") || "[]");
    saved.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("inf_leads", JSON.stringify(saved));
    toast({ title: "Message received", description: "Our experts will get back to you within 24 hours." });
    setForm({ name: "", email: "", phone: "", org: "", brief: "" });
  };

  return (
    <section id="contact" className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
      <div className="container-x grid lg:grid-cols-2 gap-14">
        <div>
          <p className="eyebrow text-neutral-500">/ Get in Touch</p>
          <h2 className="mt-4 display text-4xl md:text-5xl font-extrabold uppercase leading-[0.95]">
            Book a Free <span className="hl">Consultation</span>
          </h2>
          <p className="mt-5 text-neutral-700 leading-relaxed max-w-md">
            Discover how our digital product, IT staffing, and AI-powered solutions can accelerate your outcomes.
          </p>

          <div className="mt-8 flex flex-col gap-3">
            <a href={`tel:${company.phone}`} className="flex items-center gap-4 border-2 border-[#0A0A0A] bg-white px-5 py-4 hover:shadow-[5px_5px_0_#0A0A0A] hover:-translate-y-0.5 transition-all">
              <Phone size={18} /> <span className="mono text-sm font-bold">{company.phone}</span>
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-4 border-2 border-[#0A0A0A] bg-white px-5 py-4 hover:shadow-[5px_5px_0_#0A0A0A] hover:-translate-y-0.5 transition-all">
              <Mail size={18} /> <span className="mono text-sm font-bold">{company.email}</span>
            </a>
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {offices.map((o) => (
              <div key={o.country} className="border-2 border-[#0A0A0A] bg-white p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <MapPin size={13} /> <p className="mono text-[10px] font-bold uppercase tracking-wide">{o.country}</p>
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed">{o.address}</p>
                <p className="mono text-xs font-bold mt-2">{o.phone}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="border-2 border-[#0A0A0A] bg-[#0A0A0A] p-8 shadow-[10px_10px_0_#0A0A0A]">
          <form onSubmit={submit} className="space-y-5 bg-white p-6 border-2 border-[#0A0A0A]">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name *" value={form.name} onChange={set("name")} placeholder="John Doe" />
              <Field label="Work Email *" type="email" value={form.email} onChange={set("email")} placeholder="john@company.com" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Phone" value={form.phone} onChange={set("phone")} placeholder="+1 000 000 0000" />
              <Field label="Organization" value={form.org} onChange={set("org")} placeholder="Company" />
            </div>
            <div>
              <label className="mono text-[11px] uppercase tracking-widest text-neutral-500 block mb-2">Project Brief *</label>
              <textarea value={form.brief} onChange={set("brief")} placeholder="Tell us about your project..." className="w-full border-2 border-[#0A0A0A] bg-white px-4 py-3 text-sm min-h-[120px] focus:outline-none focus:shadow-[4px_4px_0_#0A0A0A] transition-shadow" />
            </div>
            <button type="submit" className="btn-ink w-full group">
              Talk to Our Experts <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
